import React, { useState, useEffect } from 'react';
import { FaPlus, FaExternalLinkAlt, FaPlay } from 'react-icons/fa';

const topics = {
  bible: [
    "1_Corinthians.json", "1_John.json", "1_Peter.json", "1_Thessalonians.json",
    "1_Timothy.json", "2_Corinthians.json", "2_John.json", "2_Peter.json",
    "2_Thessalonians.json", "2_Timothy.json", "3_John.json", "Acts.json",
    "Colossians.json", "Ephesians.json", "Galatians.json", "Hebrews.json",
    "James.json", "John.json", "Jude.json", "Luke.json", "Mark.json",
    "Matthew.json", "Philemon.json", "Philippians.json", "Revelation.json",
    "Romans.json", "Titus.json"
  ],
  message: [
    "1_John.json", "2_John.json", "3_John.json", "Jude.json"
  ],
  topic: [
    "discipline.json", "evangelism.json", "faith.json", "forgiveness.json",
    "friendship.json", "gratitude.json", "healing.json", "hope.json",
    "humility.json", "justice.json", "leadership.json", "love.json",
    "marriage.json", "obedience.json", "patience.json", "perseverance.json",
    "prayer.json", "service.json", "wisdom.json", "worship.json"
  ],
  research: {
    "1_Corinthians": [
      "1_Corinthians_1.json", "1_Corinthians_2.json", "1_Corinthians_3.json",
      "1_Corinthians_4.json", "1_Corinthians_5.json"
    ]
  }
};

const PresentationLinkGenerator = ({ addToSession, onPreviewDeck }) => {
  const [selectedTopic, setSelectedTopic] = useState('bible');
  const [selectedFile, setSelectedFile] = useState('');
  const [selectedMode, setSelectedMode] = useState('Play');
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [gifDelay, setGifDelay] = useState(20000);
  const [iconSize, setIconSize] = useState(34);
  const [masterColor, setMasterColor] = useState('#bd5151');
  const [subView, setSubView] = useState('card');
  const [descriptions, setDescriptions] = useState(1);
  const [slideRange, setSlideRange] = useState('');
  const [disableLoop, setDisableLoop] = useState(true);

  useEffect(() => {
    if (selectedTopic && selectedFile) {
      const file = selectedTopic === 'research' ? selectedFile : `${selectedTopic}/${selectedFile.replace('.json', '')}`;
      const params = new URLSearchParams({
        file,
        mode: selectedMode,
        gifDelay,
        iconSize,
        master: masterColor,
        subView,
        descriptions,
      });
      if (slideRange.trim()) {
        params.set('slides', slideRange.trim());
      }
      if (disableLoop) {
        params.set('disableLoop', '1');
      }
      const url = `https://globalchristiancommunity.com/present?${params.toString()}`;
      setGeneratedUrl(url);
    } else {
      setGeneratedUrl('');
    }
  }, [selectedTopic, selectedFile, selectedMode, gifDelay, iconSize, masterColor, subView, descriptions, slideRange, disableLoop]);

  const handleTopicChange = (e) => {
    setSelectedTopic(e.target.value);
    setSelectedFile('');
  };

  const renderFileOptions = () => {
    if (!selectedTopic) return null;

    if (selectedTopic === 'research') {
        const researchTopics = Object.keys(topics.research);
        return researchTopics.map(subTopic => (
            <optgroup label={subTopic} key={subTopic}>
                {topics.research[subTopic].map(file => (
                    <option key={file} value={`${subTopic}/${file.replace('.json', '')}`}>{file.replace('.json', '')}</option>
                ))}
            </optgroup>
        ));
    }

    const files = topics[selectedTopic];
    if (!files) return null;

    return files.map(file => (
      <option key={file} value={file}>{file.replace('.json', '')}</option>
    ));
  };

  const buildDeckItem = () => {
    if (!selectedTopic || !selectedFile || !generatedUrl) return null;
    const file =
      selectedTopic === 'research'
        ? selectedFile
        : `${selectedTopic}/${selectedFile.replace('.json', '')}`;
    const normalized = file.replace(/^[\\/]+/, '').replace(/[\\/]+/g, '/');
    const segments = normalized.split('/');
    const topicPart = segments.length > 1 ? segments[0] : selectedTopic;
    const filePart = segments.length > 1 ? segments.slice(1).join('/') : normalized;
    return {
      sourceId: `present-custom-${file}`,
      type: 'presentationRemote',
      title: selectedFile.replace('.json', ''),
      description: `Custom presentation deck from ${topicPart}`,
      embedUrl: generatedUrl,
      originalUrl: generatedUrl,
      presentTopic: topicPart,
      presentFile: filePart,
      slideRange: slideRange.trim(),
      disableLoop,
    };
  };

  const handleAddToSession = () => {
    const item = buildDeckItem();
    if (!item) return;
    addToSession(item);
  };

  const handleOpenPreview = () => {
    const item = buildDeckItem();
    if (!item) return;
    if (onPreviewDeck) {
      onPreviewDeck(item);
      return;
    }
    window.open(item.embedUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="presentation-link-generator">
      <div className="present-header">
        <div>
          <h3>Present Decks</h3>
          <p>Pull decks from GCC Present slideshow.</p>
        </div>
        <div className="present-actions">
          <button
            type="button"
            onClick={handleAddToSession}
            disabled={!selectedTopic || !selectedFile}
          >
            <FaPlus /> Add Deck
          </button>
          <button type="button" onClick={handleOpenPreview} disabled={!generatedUrl}>
            <FaPlay /> Preview
          </button>
          {generatedUrl && (
            <a href={generatedUrl} target="_blank" rel="noopener noreferrer" className="present-action-link">
              <FaExternalLinkAlt /> Link
            </a>
          )}
        </div>
      </div>
      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="topic-select">Topic</label>
          <select
            id="topic-select"
            value={selectedTopic}
            onChange={handleTopicChange}
            aria-label="Topic"
          >
            {Object.keys(topics).map(topic => (
              <option key={topic} value={topic}>{topic}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="file-select">File</label>
          <select
            id="file-select"
            value={selectedFile}
            onChange={e => setSelectedFile(e.target.value)}
            disabled={!selectedTopic}
            aria-label="Deck file"
          >
            <option value="">Select a file</option>
            {renderFileOptions()}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="mode-select">Mode</label>
          <select id="mode-select" value={selectedMode} onChange={e => setSelectedMode(e.target.value)}>
            <option value="Play">Play</option>
            <option value="Play-Topic">Play-Topic</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="slide-range">Slide Range (optional)</label>
          <input
            id="slide-range"
            type="text"
            value={slideRange}
            onChange={e => setSlideRange(e.target.value)}
            placeholder="e.g. 2-5 or 3"
          />
        </div>
        <div className="form-group checkbox-field">
          <label htmlFor="disable-loop-toggle" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input
              id="disable-loop-toggle"
              type="checkbox"
              checked={disableLoop}
              onChange={(e) => setDisableLoop(e.target.checked)}
            />
            Disable loop
          </label>
        </div>
        <div className="form-group">
          <label htmlFor="gif-delay">GIF Delay (ms)</label>
          <input
            id="gif-delay"
            type="number"
            value={gifDelay}
            onChange={e => setGifDelay(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="icon-size">Icon Size</label>
          <input
            id="icon-size"
            type="number"
            value={iconSize}
            onChange={e => setIconSize(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="master-color">Master Color</label>
          <input
            id="master-color"
            type="color"
            value={masterColor}
            onChange={e => setMasterColor(e.target.value)}
            aria-label="Master color"
          />
        </div>
        <div className="form-group">
          <label htmlFor="sub-view">Visual Type</label>
          <input
            id="sub-view"
            type="text"
            value={subView}
            onChange={e => setSubView(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="descriptions-select">Descriptions</label>
          <select
            id="descriptions-select"
            value={descriptions}
            onChange={e => setDescriptions(e.target.value)}
            aria-label="Descriptions toggle"
          >
            <option value="1">On</option>
            <option value="0">Off</option>
          </select>
        </div>
      </div>
      {generatedUrl && (
        <div className="generated-url">
          <label>Generated URL:</label>
          <input type="text" value={generatedUrl} readOnly />
        </div>
      )}
    </div>
  );
};

export default PresentationLinkGenerator;
